<template>
  <div>
    <header
      class="itinerary-list-header flex items-center justify-center px-6 py-3 mdsm:px-4 rounded lg:w-full"
    >
      <div class="relative inline-block sm:w-full lg:w-full">
        <responsive>
          <template #lg>
            <div
              :class="trips.length > 0 ? 'justify-between' : 'justify-center'"
              class="flex items-center"
            >
              <button
                v-if="trips.length > 0"
                type="button"
                class="btn bg-white input-tall liveaboard-nav-button rounded border border-black-30"
                :disabled="!canGoPrev"
                @click="goToPrevMonth"
              >
                <div class="flex items-center">
                  <svg
                    width="24"
                    height="24"
                    class="mr-2 -ml-2"
                    fill="currentColor"
                  >
                    <use xlink:href="/img/sprite.svg#ic_chevron-left"></use>
                  </svg>
                  <span class="font-medium"
                    >Previous <span class="mdsm:hidden">month</span></span
                  >
                </div>
              </button>

              <div class="flex">
                <liveaboard-guest-picker
                  class="guest-picker-desktop"
                  :class="{ 'guest-picker-error': isMissingGuestInfo }"
                  ref="guestPicker"
                  v-model="guest"
                />
                <month-picker
                  class="ml-4 month-picker-desktop"
                  v-model="selectedTime"
                />
              </div>

              <button
                v-if="trips.length > 0"
                type="button"
                class="btn bg-white input-tall liveaboard-nav-button rounded border border-black-30"
                @click="goToNextMonth"
              >
                <div class="flex items-center">
                  <span class="font-medium"
                    >Next <span class="mdsm:hidden">month</span></span
                  >
                  <svg
                    width="24"
                    height="24"
                    class="ml-2 -mr-2"
                    fill="currentColor"
                  >
                    <use xlink:href="/img/sprite.svg#ic_chevron-right"></use>
                  </svg>
                </div>
              </button>
            </div>
          </template>
          <template #mdsm>
            <div class="grid grid-cols-2 gap-4">
              <liveaboard-guest-picker
                class="guest-picker-mobile"
                ref="guestPicker"
                v-model="guest"
              />
              <mobile-month-picker class="" v-model="selectedTime" />
            </div>
          </template>
        </responsive>
      </div>
    </header>

    <ul
      v-if="!isLoading && trips.length"
      class="sm:-mx-6 trip-list list-reset border border-black-30 rounded mt-3 sm:border-none mdsm:mt-0"
    >
      <li v-for="trip in trips" :key="trip.id">
        <responsive>
          <template #lgmd>
            <trip
              :supplier="supplier"
              :trip="trip"
              :selected-time="selectedTime"
              @enquire-click="handleEnquireClick(trip)"
            />
          </template>

          <template #sm>
            <trip-mobile
              :supplier="supplier"
              :trip="trip"
              :selected-time="selectedTime"
              @enquire-click="handleEnquireClick(trip)"
            />
            <hr class="trip-separator my-8 mdsm:hidden" />
          </template>
        </responsive>
      </li>
    </ul>

    <transition name="loading-trips">
      <loading-trip v-if="isLoading" />
    </transition>

    <no-trip
      class="mt-4"
      :selected-time="selectedTime"
      :resort-id="resortId"
      :country-slug="countrySlug"
      :guest="guest"
      v-show="!trips.length"
      @change="setSelectedTime"
    />

    <div
      v-if="trips.length > 0"
      class="lg:hidden control-buttons flex items-center justify-between rounded bg-black-light-1 mt-4 py-4 px-6 border border-black-light-2 mdsm:mt-0 mdsm:-mx-6 mdsm:rounded-none mdsm:border-0"
    >
      <button
        type="button"
        class="btn bg-white input-tall liveaboard-nav-button rounded border border-black-30"
        :disabled="!canGoPrev"
        @click="goToPrevMonth"
      >
        <div class="flex items-center">
          <svg width="24" height="24" class="mr-2 -ml-2" fill="currentColor">
            <use xlink:href="/img/sprite.svg#ic_chevron-left"></use>
          </svg>
          <span class="font-medium"
            >Previous <span class="mdsm:hidden">month</span></span
          >
        </div>
      </button>

      <button
        type="button"
        class="btn bg-white input-tall liveaboard-nav-button rounded border border-black-30"
        @click="goToNextMonth"
      >
        <div class="flex items-center">
          <span class="font-medium"
            >Next <span class="mdsm:hidden">month</span></span
          >
          <svg width="24" height="24" class="ml-2 -mr-2" fill="currentColor">
            <use xlink:href="/img/sprite.svg#ic_chevron-right"></use>
          </svg>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import NoTrip from "./NoTrip";
import LoadingTrip from "./LoadingTrip";
import Trip from "./Trip";
import TripMobile from "./TripMobile";
import MonthPicker from "./MonthPicker";
import MobileMonthPicker from "./MobileMonthPicker";
import Responsive from "../../components/core/Responsive";
import { fetchFromURL } from "../../helpers/topNavHelpers";
import { syncURLParams } from "../../helpers/searchHelpers";
import { getSelectedCurrency } from "../../helpers";

export default {
  components: {
    LoadingTrip,
    MonthPicker,
    MobileMonthPicker,
    Trip,
    TripMobile,
    NoTrip,
    Responsive,
  },
  props: ["countrySku", "countrySlug", "resortId"],
  data() {
    const now = moment().format("YYYY-MM-DD");
    let selectedTime = now;
    let guest = "";

    // Try getting values from user search settings
    const userSearchSettings = fetchFromURL();
    if (
      userSearchSettings.formData.dates[0] &&
      moment(userSearchSettings.formData.dates[0]).isValid()
    ) {
      selectedTime = userSearchSettings.formData.dates[0];
    }

    if (userSearchSettings.formData.guests !== "0") {
      guest = userSearchSettings.formData.guests;
    }



    if (moment(selectedTime, "YYYY-MM-DD").isBefore(moment())) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("from");
      syncURLParams(searchParams);

      selectedTime = now;
    }

    return {
      selectedTime,
      guest,
      supplier: {},
      trips: [],
      isLoading: true,
      isMissingGuestInfo: false,
      minimumDate: moment().startOf("month"),
    };
  },
  computed: {
    canGoPrev() {
      const mmSelectedTime = moment(this.selectedTime).startOf("month");

      return mmSelectedTime.isAfter(this.minimumDate);
    },
  },
  methods: {
    goToPrevMonth() {
      const mmValue = moment(this.selectedTime);
      const prevMonth = mmValue.add(-1, "months").startOf("month");

      this.selectedTime = prevMonth.format("YYYY-MM-DD");
    },
    goToNextMonth() {
      const mmValue = moment(this.selectedTime);
      const nextMonth = mmValue.add(1, "months").startOf("month");

      this.selectedTime = nextMonth.format("YYYY-MM-DD");
    },
    setSelectedTime(newTime) {
      this.selectedTime = newTime;
    },
    handleEnquireClick(trip) {
      const selectedCurrency = getSelectedCurrency();

      if (window.matchMedia("(min-width: 1201px)").matches) {
        if (this.guest === "") {
          this.isMissingGuestInfo = true;
          const ele = document.querySelector(".itinerary-list-header");
          window.scrollTo({ top: ele.offsetTop, behavior: "smooth" });

          this.$refs.guestPicker.ask();
        } else {
          this.isMissingGuestInfo = false;

          const params = `?supplier_id=${this.resortId}&trip_id=${
            trip.id
          }&guest_num=${this.guest}&_zb-sid=${
            window.Craft.session.sign || ""
          }&currency=${selectedCurrency.code}`;
          window.location.href = `${window.ZUBLU_VARIABLES.BOOKING_APP}/l/direct${params}`;
        }
      } else {
        // mobile or tablet
        if (this.guest === "") {
          this.$refs.guestPicker.ask();
        } else {
          const params = `?supplier_id=${this.resortId}&trip_id=${
            trip.id
          }&guest_num=${this.guest}&_zb-sid=${
            window.Craft.session.sign || ""
          }&currency=${selectedCurrency.code}`;
          window.location.href = `${window.ZUBLU_VARIABLES.BOOKING_APP}/l/direct${params}`;
        }
      }
    },
    getPrices(prices) {
      const validPrice = prices.find((_p) => _p.lowest_price);
      return [validPrice.real_price, validPrice.final_price];
    },
    updateJsonLdProduct(trips) {
      const jsonLdProduct = document.getElementById("jsonLdProduct");

      if (!jsonLdProduct) {
        return;
      }

      let data = null;
      try {
        data = JSON.parse(jsonLdProduct.text);
      } catch (err) {
        return;
      }

      const offers = [];
      trips.forEach((_t) => {
        offers.push({
          "@type": "AggregateOffer",
          name: _t.itineraries.name,
          priceCurrency: "USD",
          lowPrice: `${this.getPrices(_t.prices)[1]}`,
          highPrice: `${this.getPrices(_t.prices)[0]}`,
          offerCount: `${trips.length}`,
          availability: "https://schema.org/OnlineOnly",
          businessFunction: "https://www.purl.org/goodrelations/v1#LeaseOut",
          itemOffered: `${data.name} Liveaboards in ${moment(
            this.selectedTime
          ).format("YYYY MMM")}`,
          url: `${data.url}?from=${moment(this.selectedTime).format(
            "YYYY-MM-DD"
          )}`,
        });
      });

      data.offers = offers;
      jsonLdProduct.replaceChildren(JSON.stringify(data, null, 4));
    },
  },
  mounted() {
    window
      .fetchSupplierInfo(this.resortId)
      .then((response) => {
        this.supplier = response.supplier;
      })
      .catch(() => {
        this.supplier = {};
      });
  },
  watch: {
    guest(value) {
      if (value !== "") {
        this.isMissingGuestInfo = false;
      }
    },
    selectedTime: {
      handler(value) {
        this.isLoading = true;
        const mmValue = moment(value);

        const month = mmValue.month() + 1;
        const year = mmValue.year();
        const url = `${window.ZUBLU_VARIABLES.ODYSSEY_API}/api/v1/liveaboard/get_trip/month/${month}/${year}/${this.resortId}`;

        const getTripPromise = new Promise((resolve, reject) => {
          $.get(url).done(resolve).fail(reject);
        });

        getTripPromise
          .then((results) => {
            if (Array.isArray(results.data)) {
              this.updateJsonLdProduct(results.data);
              this.trips = results.data;
            } else {
              this.updateJsonLdProduct([]);
              this.trips = [];
            }

            this.isLoading = false;
          })
          .catch(() => {
            this.updateJsonLdProduct([]);
            this.trips = [];
            this.isLoading = false;
          });
      },
      immediate: true,
    },
  },
};
</script>

<style>
.guest-picker-error input.input {
  border-color: var(--error-primary);
}
</style>

<style scoped>
.guest-picker-desktop ::v-deep input {
  width: 220px;
}

.month-picker-desktop ::v-deep .month-trigger {
  width: 220px;
}

.control-buttons button:disabled {
  color: var(--black-light-3);
}

.liveaboard-nav-button:disabled {
  background-color: var(--black-light-1);
}

@media (max-width: 1200px) and (min-width: 600px) {
  .itinerary-list-header {
    border: 0;
  }
}

@media (max-width: 600px) {
  .trip-separator {
    height: 16px;
    background-color: #f6f6f6;
    border: 0px;
  }

  .trip-list {
    background-color: #f6f6f6;
  }

  .trip-list li {
    background: white;
    padding-top: 24px;
    padding-bottom: 24px;
    margin-bottom: 16px;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.16);
  }

  .trip-list li:last-child {
    margin-bottom: 0;
  }

  .itinerary-list-header {
    align-items: flex-start;
    height: auto;
    border: 0;
  }

  .liveaboard-nav-button {
    border: 1px solid #dcdcdc;
  }
}

.guest-picker-mobile {
  display: flex !important;
}

.guest-picker-mobile > ::v-deep div.relative {
  width: 100%;
}
</style>
